
import React, { useState } from 'react';
import { useOperationsHead } from './hooks/useOperationsHead';

const DiscountCategoryManager = () => {
  const { 
    productCategories, 
    stoneTypes, 
    createCategory, 
    updateCategory, 
    assignProductsToCategory,
    updateStoneDiscounts 
  } = useOperationsHead();

  const [newCategory, setNewCategory] = useState({
    name: 'Exclusive',
    va_from: '',
    va_to: '',
    se_discount: '',
    fm_discount: '',
    asrm_discount: '',
    srm_discount: '',
    oh_discount: ''
  });

  const [stoneDiscounts, setStoneDiscounts] = useState({});
  const [activeTab, setActiveTab] = useState('gold');

  const categoryOptions = [
    'Exclusive',
    'Premium', 
    'Fast moving',
    'Economy',
    'Regular'
  ];

  const handleCreateCategory = async () => {
    try {
      await createCategory.mutateAsync(newCategory);
      setNewCategory({
        name: 'Exclusive',
        va_from: '',
        va_to: '',
        se_discount: '',
        fm_discount: '',
        asrm_discount: '',
        srm_discount: '',
        oh_discount: ''
      });
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const handleAssignProducts = async (categoryId: string, vaFrom: number, vaTo: number) => {
    try {
      await assignProductsToCategory.mutateAsync({ categoryId, vaFrom, vaTo });
    } catch (error) {
      console.error('Error assigning products:', error);
    }
  };

  return (
    <div className="p-6 bg-white">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Product Category & Discount Management</h1>
        <p className="text-gray-600">Set discount levels for different product categories and personnel roles</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6">
        <button
          onClick={() => setActiveTab('gold')}
          className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap cursor-pointer ${
            activeTab === 'gold' 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Gold Categories (% on VA)
        </button>
        <button
          onClick={() => setActiveTab('stones')}
          className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap cursor-pointer ${
            activeTab === 'stones' 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Stone Discounts (₹ Amount)
        </button>
      </div>

      {activeTab === 'gold' && (
        <div className="space-y-6">
          {/* Create New Category */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Create New Product Category</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name
                </label>
                <select
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {categoryOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  VA From (₹)
                </label>
                <input
                  type="number"
                  value={newCategory.va_from}
                  onChange={(e) => setNewCategory({...newCategory, va_from: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  VA To (₹)
                </label>
                <input
                  type="number"
                  value={newCategory.va_to}
                  onChange={(e) => setNewCategory({...newCategory, va_to: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="50000"
                />
              </div>
            </div>

            <div className="mb-4">
              <h3 className="text-md font-medium text-gray-800 mb-3">Discount Percentages on Value Addition (VA)</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SE Discount (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={newCategory.se_discount}
                    onChange={(e) => setNewCategory({...newCategory, se_discount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="2.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    FM Discount (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={newCategory.fm_discount}
                    onChange={(e) => setNewCategory({...newCategory, fm_discount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="3.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ASRM Discount (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={newCategory.asrm_discount}
                    onChange={(e) => setNewCategory({...newCategory, asrm_discount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="4.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SRM Discount (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={newCategory.srm_discount}
                    onChange={(e) => setNewCategory({...newCategory, srm_discount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="5.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OH Discount (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={newCategory.oh_discount}
                    onChange={(e) => setNewCategory({...newCategory, oh_discount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="7.0"
                  />
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-md mb-4">
              <p className="text-sm text-blue-800">
                <i className="ri-information-line mr-2"></i>
                <strong>Note:</strong> Discounts are applied as percentage on Value Addition (VA) for gold items. 
                After creating the category, click "Assign Products" to automatically assign all products within the VA range.
              </p>
            </div>

            <button
              onClick={handleCreateCategory}
              disabled={createCategory.isPending}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {createCategory.isPending ? 'Creating...' : 'Create Category'}
            </button>
          </div>

          {/* Existing Categories */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Existing Product Categories</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      VA Range
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SE %
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      FM %
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ASRM %
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SRM %
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      OH %
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Products
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {productCategories.data?.map((category: any) => (
                    <tr key={category.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          category.name === 'Exclusive' ? 'bg-purple-100 text-purple-800' :
                          category.name === 'Premium' ? 'bg-blue-100 text-blue-800' :
                          category.name === 'Fast moving' ? 'bg-green-100 text-green-800' :
                          category.name === 'Economy' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {category.name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        ₹{category.va_from?.toLocaleString()} - ₹{category.va_to?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {category.se_discount}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {category.fm_discount}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {category.asrm_discount}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {category.srm_discount}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {category.oh_discount}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {category.product_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleAssignProducts(category.id, category.va_from, category.va_to)}
                          className="text-blue-600 hover:text-blue-900 mr-3 cursor-pointer whitespace-nowrap"
                        >
                          Assign Products
                        </button>
                        <button className="text-green-600 hover:text-green-900 cursor-pointer whitespace-nowrap">
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'stones' && (
        <div className="space-y-6">
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Diamond & Precious Stone Discount Management</h2>
            <div className="bg-amber-50 p-4 rounded-md mb-4">
              <p className="text-sm text-amber-800">
                <i className="ri-gem-line mr-2"></i>
                <strong>Note:</strong> Stone discounts are set as fixed amounts in ₹ (Rupees) for diamonds and precious stones, 
                not as percentages. These amounts will be deducted from the stone price for each personnel role.
              </p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stone Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SE (₹)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      FM (₹)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ASRM (₹)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SRM (₹)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      OH (₹)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stoneTypes.data?.map((stone: any) => (
                    <tr key={stone.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          <i className="ri-gem-line mr-2 text-purple-600"></i>
                          {stone.stone_type}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          value={stoneDiscounts[`${stone.id}_se`] || stone.se_discount || ''}
                          onChange={(e) => setStoneDiscounts({
                            ...stoneDiscounts,
                            [`${stone.id}_se`]: e.target.value
                          })}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          value={stoneDiscounts[`${stone.id}_fm`] || stone.fm_discount || ''}
                          onChange={(e) => setStoneDiscounts({
                            ...stoneDiscounts,
                            [`${stone.id}_fm`]: e.target.value
                          })}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="750"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          value={stoneDiscounts[`${stone.id}_asrm`] || stone.asrm_discount || ''}
                          onChange={(e) => setStoneDiscounts({
                            ...stoneDiscounts,
                            [`${stone.id}_asrm`]: e.target.value
                          })}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="1000"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          value={stoneDiscounts[`${stone.id}_srm`] || stone.srm_discount || ''}
                          onChange={(e) => setStoneDiscounts({
                            ...stoneDiscounts,
                            [`${stone.id}_srm`]: e.target.value
                          })}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="1500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          value={stoneDiscounts[`${stone.id}_oh`] || stone.oh_discount || ''}
                          onChange={(e) => setStoneDiscounts({
                            ...stoneDiscounts,
                            [`${stone.id}_oh`]: e.target.value
                          })}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="2000"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => updateStoneDiscounts.mutateAsync({
                            stoneId: stone.id,
                            discounts: {
                              se_discount: stoneDiscounts[`${stone.id}_se`] || stone.se_discount,
                              fm_discount: stoneDiscounts[`${stone.id}_fm`] || stone.fm_discount,
                              asrm_discount: stoneDiscounts[`${stone.id}_asrm`] || stone.asrm_discount,
                              srm_discount: stoneDiscounts[`${stone.id}_srm`] || stone.srm_discount,
                              oh_discount: stoneDiscounts[`${stone.id}_oh`] || stone.oh_discount
                            }
                          })}
                          className="text-blue-600 hover:text-blue-900 cursor-pointer whitespace-nowrap"
                        >
                          Update
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountCategoryManager;
